import { Link, useNavigate } from 'react-router-dom'
import { Settings, Shield, LogOut, Globe, Check, Crown } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from '@/lib/store/auth'
import { useLanguage } from '@/i18n/use-language'
import { Button } from './button'
import { Logo } from './logo'
import { Avatar, AvatarImage, AvatarFallback } from './avatar'
import { ThemeToggle } from './theme-toggle'
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from './dropdown-menu'

export function Navbar() {
  const { user, isAuthenticated, logout, openLoginDialog } = useAuthStore()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { lang, setLang } = useLanguage()

  const handleLogout = () => {
    logout()
    navigate('/')
  }

  const initials = user?.displayName
    ? user.displayName.split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase()
    : user?.email?.[0]?.toUpperCase() ?? '?'

  const languageSwitcher = (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={t('nav.language')}
          className="cursor-pointer flex items-center justify-center h-8 w-8 rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
        >
          <Globe className="h-4 w-4" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-36">
        <DropdownMenuItem onClick={() => setLang('en')}>
          <span className="flex-1">English</span>
          {lang === 'en' && <Check className="ml-2 h-4 w-4" />}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => setLang('zh')}>
          <span className="flex-1">中文</span>
          {lang === 'zh' && <Check className="ml-2 h-4 w-4" />}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )

  return (
    <nav className="sticky top-0 z-50 flex h-14 items-center justify-between border-b bg-background/80 px-6 backdrop-blur-md">
      <Link to="/" aria-label={t('nav.brand')} className="-ml-1 rounded-md px-1 transition-opacity hover:opacity-80">
        <Logo size={22} />
      </Link>

      <div className="flex items-center gap-4">
        {isAuthenticated ? (
          <>
            <Link
              to="/explore"
              className="text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
            >
              {t('nav.explore')}
            </Link>
            <Link
              to="/dashboard"
              className="text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
            >
              {t('nav.workspace')}
            </Link>
            <Link
              to="/pricing"
              className="relative inline-flex items-center rounded-md border border-transparent bg-secondary px-2.5 py-0.5 text-xs font-semibold text-secondary-foreground transition-colors hover:bg-secondary/80"
            >
              {user?.tier === 'pro' && (
                <Crown className="absolute -top-1.5 -left-1.5 h-3 w-3 text-yellow-400 -rotate-12" />
              )}
              {user?.tier === 'pro' ? 'Pro' : t('billing.free')}
            </Link>
            {languageSwitcher}
            <ThemeToggle />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button type="button" className="cursor-pointer flex items-center gap-2 rounded-md px-2 py-1 transition-colors hover:bg-accent">
                <Avatar className="h-7 w-7">
                  {user?.avatarUrl && <AvatarImage src={user.avatarUrl} alt="" />}
                  <AvatarFallback className="text-[10px]">{initials}</AvatarFallback>
                </Avatar>
                <span className="text-sm text-muted-foreground">
                  {user?.displayName ?? user?.email}
                </span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuLabel className="font-normal">
                <p className="text-sm font-medium">{user?.displayName}</p>
                <p className="text-xs text-muted-foreground">{user?.email}</p>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate('/settings')}>
                <Settings className="h-4 w-4" />
                {t('nav.settings')}
              </DropdownMenuItem>
              {(user?.role === 'admin' || user?.role === 'superadmin') && (
                <DropdownMenuItem onClick={() => navigate('/admin')}>
                  <Shield className="h-4 w-4" />
                  {t('nav.admin')}
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout}>
                <LogOut className="h-4 w-4" />
                {t('nav.logout')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          </>
        ) : (
          <>
            <Link
              to="/explore"
              className="text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
            >
              {t('nav.explore')}
            </Link>
            {languageSwitcher}
            <ThemeToggle />
            <Button size="sm" onClick={openLoginDialog}>{t('nav.signIn')}</Button>
          </>
        )}
      </div>
    </nav>
  )
}
